import React from "react";
import { useAuth } from "../contexts/AuthContext";
import { getRoleLabel } from "../utils/roleUtils";
import { UserRole } from "@visitproof/types";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from "../design/dropdown";
import { SidebarItem } from "../design/sidebar";
import {
  ShieldCheckIcon,
  UserGroupIcon,
  BriefcaseIcon,
  ChevronUpDownIcon,
  CheckIcon,
  StarIcon,
} from "@heroicons/react/20/solid";

const ROLE_ICONS: Record<UserRole, React.ElementType> = {
  owner: StarIcon,
  admin: BriefcaseIcon,
  supervisor: ShieldCheckIcon,
  parent: UserGroupIcon,
};

const RoleSwitcher: React.FC = () => {
  const { availableRoles, activeRole, setActiveRole, canSwitchRoles } = useAuth();

  // Don't render if user can't switch roles
  if (!canSwitchRoles || availableRoles.length <= 1) {
    return null;
  }

  const ActiveRoleIcon = activeRole ? ROLE_ICONS[activeRole] : BriefcaseIcon;

  return (
    <div className="px-2 mb-2">
      <Dropdown>
        <DropdownButton
          as={SidebarItem}
          className="w-full"
        >
          <span className="flex items-center min-w-0 gap-3">
            <span className="flex items-center justify-center rounded-lg size-8 bg-zinc-100 dark:bg-zinc-800">
              <ActiveRoleIcon className="size-4 text-zinc-600 dark:text-zinc-400" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-medium truncate text-sm/5 text-zinc-950 dark:text-white">
                {activeRole ? getRoleLabel(activeRole) : "Select Role"}
              </span>
              <span className="block font-normal truncate text-xs/5 text-zinc-500 dark:text-zinc-400">
                Switch view
              </span>
            </span>
          </span>
          <ChevronUpDownIcon className="size-5 text-zinc-400" />
        </DropdownButton>
        <DropdownMenu className="min-w-56" anchor="top start">
          {availableRoles.map((role) => {
            const RoleIcon = ROLE_ICONS[role] || BriefcaseIcon;
            const isActive = role === activeRole;

            return (
              <DropdownItem
                key={role}
                onClick={() => setActiveRole(role)}
              >
                <RoleIcon />
                <DropdownLabel>{getRoleLabel(role)}</DropdownLabel>
                {isActive && (
                  <CheckIcon className="ml-auto size-4 text-blue-500" />
                )}
              </DropdownItem>
            );
          })}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

export default RoleSwitcher;
